import { toCurrencyTruncated, toTruncated } from '@/utilities/formatting';
import { Button } from '@simplywallst/ui-core';
import { CompanyIconSkeleton, DetailsSummary } from './styled';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import { useTracking } from 'react-tracking';
import { useInformationTranslation } from '../../hooks/useInformationTranslations';
import { CompanyLogo } from '@/components/CompanyLogo/CompanyLogo';

interface Props {
  url?: string;
  listing: number;
  currencyISO: string;
  industryPrimaryName: string;
  industryTertiaryName: string;
  yearFounded: number;
  companyName: string;
  tickerSymbol: string;
  exchangeSymbol: string;
  currencySymbol: string;
  marketCapListing: number | null;
  marketCapPrimary: number;
  sharesOutstanding: number;
  originalMarketCap: number | null;
  skipDefer: boolean;
  hasLogo: boolean;
}

const Details = ({
  url,
  companyName,
  yearFounded,
  tickerSymbol,
  exchangeSymbol,
  currencyISO,
  marketCapListing,
  marketCapPrimary,
  sharesOutstanding,
  originalMarketCap,
  industryPrimaryName,
  industryTertiaryName,
  skipDefer,
  hasLogo,
}: Props) => {
  const { trackEvent, Track } = useTracking({
    type: 'track',
  });
  const t = useInformationTranslation();
  return (
    <Track>
      <section>
        <DeferRenderUntilIntersecting
          skipDefer={skipDefer}
          fallback={<CompanyIconSkeleton />}
        >
          {() => (
            <CompanyLogo
              website={url}
              tickerSymbol={tickerSymbol}
              size={80}
              sizeLg={100}
              hasLogo={hasLogo}
            />
          )}
        </DeferRenderUntilIntersecting>
        <DetailsSummary>
          <li>
            {t('information.details.name')}{' '}
            <span itemProp="legalName">{companyName}</span>
          </li>
          <li>
            {t('information.details.ticker')} <span>{tickerSymbol}</span>
          </li>
          <li>
            {t('information.details.exchange')} <span>{exchangeSymbol}</span>
          </li>
          <li>
            {t('information.details.founded')}{' '}
            <span itemProp="foundingDate">{yearFounded}</span>
          </li>
          <li>
            {t('information.details.industry')}{' '}
            <span itemProp="industry">{industryTertiaryName}</span>
          </li>
          <li>
            {t('information.details.sector')}{' '}
            <span itemProp="sector">{industryPrimaryName}</span>
          </li>
          <li>
            {typeof originalMarketCap === 'number' && originalMarketCap > 0
              ? t('information.details.impliedMarketCap')
              : t('information.details.marketCap')}{' '}
            <span>{toCurrencyTruncated(marketCapPrimary, 3, currencyISO)}</span>
          </li>
          {originalMarketCap !== null && originalMarketCap > 0 && (
            <li>
              {t('information.details.marketCap')}{' '}
              <span>
                {toCurrencyTruncated(originalMarketCap, 3, currencyISO)}
              </span>
            </li>
          )}
          {marketCapListing !== null &&
            marketCapListing !== marketCapPrimary && (
              <li>
                {t('information.details.listingMarketCap')}{' '}
                <span>
                  {toCurrencyTruncated(marketCapListing, 3, currencyISO)}
                </span>
              </li>
            )}
          <li>
            {t('information.details.sharesOutstanding')}{' '}
            <span>{toTruncated(sharesOutstanding)}</span>
          </li>
          {typeof url !== 'undefined' && url !== '' && (
            <li>
              {t('information.details.website')}{' '}
              <Button
                as="a"
                styleType="strongLink"
                href={url}
                rel="nofollow noreferrer noopener"
                target="_blank"
                onClick={() =>
                  trackEvent({
                    action: 'click',
                    modifier: 'website',
                  })
                }
              >
                {url}
              </Button>
            </li>
          )}
        </DetailsSummary>
      </section>
    </Track>
  );
};

export default Details;
